import { useMemo, useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { Column, Connection, Id } from "./types";
import { useSortable, SortableContext } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ConnectionCard from "./ConnectionCard";
import { styles } from "../../../../styles/themes/style";
import Text from "../../../../components/Text";
import ConnectionAutocompleteSelect from "../../../../fragments/ConnectionAutocompleteSelect";
import KanbanColumnActionDropdown from "../../../../fragments/dropdowns/KanbanColumnActionDropdown";
import LoadingSpinner from "../../../../components/LoadingSpinner";
import Row from "../../../../components/Row";
import { IKanban } from "../../../../interfaces/models/kanban";

interface Props {
  column: Column;
  connections: Connection[];
  updateColumn: (id: Id, title: string, color: string) => void;
  addConnectionToColumn: (columnId: Id, event: any) => void;
  removeConnection: (id: Id) => void;
  connectionClicked: (connection: Connection) => void;
  removeColumn: (column: Column) => void;
  changeColumnColor: (column: Column) => void;
  onLoadMore: () => void;
  loading: boolean;
  hasMore: boolean;
  currentKanbanId?: string;
  pipelineKanbans?: IKanban[];
  activePipeline?: IKanban;
}

const ColumnContainer = (props: Props) => {
  const [editMode, setEditMode] = useState(false);
  const {
    column,
    updateColumn,
    addConnectionToColumn,
    connections,
    removeConnection,
    connectionClicked,
    removeColumn,
    changeColumnColor,
    onLoadMore,
    loading,
    hasMore,
  } = props;
  const connectionCount = connections.length;
  const connectionIds = useMemo(
    () => connections.map((connection) => connection.id),
    [connections]
  );
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (loading && scrollRef.current) {
      const { scrollHeight } = scrollRef.current;
      scrollRef.current.scrollTo({
        top: scrollHeight,
        behavior: "smooth",
      });
    }
  }, [loading]);

  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: column.id,
    data: { type: "Column", column },
    disabled: editMode,
  });

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    // Load more when scrolled to bottom (with 50px buffer)
    if (scrollHeight - scrollTop <= clientHeight + 50 && !loading && hasMore) {
      onLoadMore();
    }
  };

  return (
    <ColumnWrapper
      ref={setNodeRef}
      style={{ transition, transform: CSS.Transform.toString(transform) }}
      isDragging={isDragging}
    >
      <ColumnHeader
        {...attributes}
        {...listeners}
        $backgroundColor={column?.color}
        onClick={() => setEditMode(true)}
      >
        <div
          style={{
            flex: 1,
            display: "flex",
            gap: "8px",
            alignItems: "center",
          }}
        >
          {!editMode ? (
            <>
              <Text $white $bold>
                {column.title}
              </Text>
              {connectionCount > 0 && (
                <CountBadge aria-label={`Count: ${connectionCount}`}>
                  {connectionCount}
                </CountBadge>
              )}
            </>
          ) : (
            <input
              autoFocus
              style={{
                backgroundColor: "white",
                border: `1px solid ${styles.colors.primary600}`,
                borderRadius: "4px",
                padding: "4px",
                outline: "none",
              }}
              defaultValue={column.title}
              // onChange={(e) => updateColumn(column.id, e.target.value)}
              onBlur={() => setEditMode(false)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const target = e.target as HTMLInputElement;
                  updateColumn(column.id, target.value, column.color);
                  setEditMode(false);
                }
              }}
            />
          )}
        </div>
        <div
          className="column-actions-wrapper"
          style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}
          onClick={(e: any) => e.stopPropagation()}
        >
          <KanbanColumnActionDropdown
            column={column}
            onDeleteHandler={removeColumn}
            onChangeColorHandler={changeColumnColor}
          />
        </div>
      </ColumnHeader>
      <ConnectionAutocompleteSelectWrapper>
        <ConnectionAutocompleteSelect
          showOnFocus={true}
          width={"309px"}
          showValue={false}
          onChangeHandler={(e: any) => addConnectionToColumn(column.id, e)}
        />
      </ConnectionAutocompleteSelectWrapper>
      <ColumnContent ref={scrollRef} onScroll={handleScroll}>
        <SortableContext items={connectionIds}>
          {connections.map((connection) => (
            <ConnectionCard
              key={connection.id}
              connection={connection}
              removeConnection={removeConnection}
              connectionClicked={connectionClicked}
              currentKanbanId={props.currentKanbanId}
              pipelineKanbans={props.pipelineKanbans}
              activePipeline={props.activePipeline}
            />
          ))}
        </SortableContext>
        {loading && (
          <Row justifyCenter style={{ padding: '10px 0', width: '100%' }}>
             <LoadingSpinner />
          </Row>
        )}
      </ColumnContent>
    </ColumnWrapper>
  );
};

const ColumnWrapper = styled.div<{ isDragging: boolean }>`
  background-color: white;
  width: 325px;
  min-width: 325px;
  height: 100%;
  min-height: 0;
  flex-shrink: 0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  ${({ isDragging }) =>
    isDragging && `border: 1px solid ${styles.colors.primary600};`}
`;

const ColumnHeader = styled.div<{ $backgroundColor?: string }>`
  height: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 4px 4px 0 0;
  padding: 0px 8px;
  color: ${styles.colors.white};
  background-color: ${({ $backgroundColor }) => $backgroundColor ?? styles.colors.primary600};
  
  /* Style threeDots icon in the dropdown button */
  .column-actions-wrapper .dropdown-toggle svg path {
    fill: ${styles.colors.white} !important;
    opacity: 1;
    transition: opacity 0.2s;
  }
  &:hover .column-actions-wrapper .dropdown-toggle svg path {
    fill: ${styles.colors.white} !important;
    opacity: 0.7;
  }
`;

const CountBadge = styled.span`
  height: 18px;
  min-width: 18px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  color: ${styles.colors.white};
  background: rgba(255, 255, 255, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.25);
  user-select: none;
`;

const ColumnContent = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  overflow-x: hidden;
  overflow-y: auto;
  min-height: 0;
  height: 0;
  
  /* Force scrollbar to always be visible on macOS */
  &::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${styles.colors.black100};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${styles.colors.black300};
    border-radius: 4px;
    min-height: 40px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${styles.colors.black400};
  }
  
  /* Firefox */
  scrollbar-width: auto;
  scrollbar-color: ${styles.colors.black300} ${styles.colors.black100};
`;
const ConnectionAutocompleteSelectWrapper = styled.div`
  position: relative;
  margin: 8px;
`;

export default ColumnContainer;
